class VariantCardsV2 extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    this.variants = [];
    this.selectedVariantId = null;
    this.refreshToken = 0;
    this.handleCardClick = this.handleCardClick.bind(this);
  }

  static get observedAttributes() {
    return ['data-variants', 'selected-variant-id', 'data-variant-id'];
  }

  connectedCallback() {
    this.shadowRoot.addEventListener('click', this.handleCardClick);
    this.refreshVariants();
  }

  disconnectedCallback() {
    this.shadowRoot.removeEventListener('click', this.handleCardClick);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected || oldValue === newValue) return;

    if (name === 'data-variants') {
      this.refreshVariants();
      return;
    }

    const variant = this.findVariant(newValue);
    if (variant) {
      this.selectedVariantId = variant.id;
      this.updateSelectedVariant();
      this.updateFormInputs(variant.id);
      this.updateVariantContent(variant);
    }
  }

  async refreshVariants() {
    const token = ++this.refreshToken;
    this.initializeVariants();
    this.mergeEmbeddedPriceData();
    this.render();

    const selectedBeforeHydration = this.getSelectedVariant();
    if (selectedBeforeHydration) {
      this.updateFormInputs(selectedBeforeHydration.id);
      this.updateVariantContent(selectedBeforeHydration);
    }

    await this.hydrateFromShopify();
    if (token !== this.refreshToken) return;

    this.mergeEmbeddedPriceData();
    this.ensureSelectedVariant();
    this.render();

    const selectedVariant = this.getSelectedVariant();
    if (selectedVariant) {
      this.updateFormInputs(selectedVariant.id);
      this.updateVariantContent(selectedVariant);
    }
  }

  initializeVariants() {
    const variantsData = this.getAttribute('data-variants');
    this.variants = [];

    if (!variantsData) return;

    try {
      const parsedVariants = JSON.parse(variantsData);
      if (!Array.isArray(parsedVariants)) {
        throw new TypeError('data-variants must contain a JSON array');
      }

      this.variants = parsedVariants
        .map((variant) => this.normalizeVariant(variant))
        .filter((variant) => variant.id);

      this.ensureSelectedVariant();
    } catch (error) {
      console.error('Error parsing variant data:', error);
    }
  }

  normalizeVariant(rawVariant = {}) {
    const featuredImage = rawVariant.featured_image || rawVariant.featuredImage;
    const imageObject = featuredImage && typeof featuredImage === 'object'
      ? featuredImage
      : null;

    const cardImage = this.firstString(
      rawVariant.card_image,
      rawVariant.cardImage,
      typeof rawVariant.image === 'string' ? rawVariant.image : null,
      rawVariant.image && rawVariant.image.src,
      imageObject && imageObject.src,
      typeof featuredImage === 'string' ? featuredImage : null
    );

    const mainImage = this.firstString(
      rawVariant.image_large,
      rawVariant.imageLarge,
      rawVariant.main_image,
      rawVariant.mainImage,
      imageObject && imageObject.src,
      typeof featuredImage === 'string' ? featuredImage : null,
      cardImage
    );

    const featuredMedia = rawVariant.featured_media || rawVariant.featuredMedia;

    return {
      ...rawVariant,
      id: rawVariant.id == null ? '' : String(rawVariant.id),
      title: String(rawVariant.title || rawVariant.name || ''),
      available: rawVariant.available !== false,
      cardImage: cardImage ? this.withImageWidth(cardImage, 160) : '',
      mainImage: mainImage ? this.withImageWidth(mainImage, 1200) : '',
      thumbnailImage: mainImage ? this.withImageWidth(mainImage, 120) : cardImage,
      imageAlt: this.firstString(
        rawVariant.image_alt,
        rawVariant.imageAlt,
        imageObject && imageObject.alt,
        rawVariant.title
      ),
      mediaId: String(
        rawVariant.media_id ||
        rawVariant.mediaId ||
        (featuredMedia && featuredMedia.id) ||
        (imageObject && imageObject.id) ||
        ''
      ),
      priceCents: this.toCents(rawVariant.price_cents ?? rawVariant.priceCents ?? rawVariant.price),
      compareAtPriceCents: this.toCents(
        rawVariant.compare_at_price_cents ??
        rawVariant.compareAtPriceCents ??
        rawVariant.compare_at_price ??
        rawVariant.compareAtPrice
      ),
      priceFormatted: this.formattedMoney(
        rawVariant.price_formatted ?? rawVariant.priceFormatted ?? rawVariant.price
      ),
      compareAtPriceFormatted: this.formattedMoney(
        rawVariant.compare_at_price_formatted ??
        rawVariant.compareAtPriceFormatted ??
        rawVariant.compare_at_price ??
        rawVariant.compareAtPrice
      )
    };
  }

  ensureSelectedVariant() {
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    const configuredVariantId =
      this.getAttribute('selected-variant-id') || this.getAttribute('data-variant-id');
    const requestedId = urlVariantId || configuredVariantId || this.selectedVariantId;

    const requestedVariant = this.findVariant(requestedId);
    const firstAvailable = this.variants.find((variant) => variant.available);
    const fallbackVariant = firstAvailable || this.variants[0];

    this.selectedVariantId = requestedVariant
      ? requestedVariant.id
      : fallbackVariant
        ? fallbackVariant.id
        : null;
  }

  mergeEmbeddedPriceData() {
    const priceDataElement = document.getElementById('variant-prices-data');
    if (!priceDataElement) return;

    try {
      const priceData = JSON.parse(priceDataElement.textContent || '{}');

      this.variants = this.variants.map((variant) => {
        const embedded = priceData[variant.id];
        if (!embedded) return variant;

        return {
          ...variant,
          available: embedded.available ?? variant.available,
          priceCents: this.toCents(embedded.price_cents ?? embedded.priceCents) ?? variant.priceCents,
          compareAtPriceCents:
            this.toCents(
              embedded.compare_at_price_cents ?? embedded.compareAtPriceCents
            ) ?? variant.compareAtPriceCents,
          priceFormatted:
            this.formattedMoney(embedded.price) || variant.priceFormatted,
          compareAtPriceFormatted:
            this.formattedMoney(embedded.compare_at_price) ||
            variant.compareAtPriceFormatted
        };
      });
    } catch (error) {
      console.warn('Unable to parse variant-prices-data:', error);
    }
  }

  async hydrateFromShopify() {
    const productPath = window.location.pathname.replace(/\/$/, '');
    if (!/\/products\/[^/]+$/.test(productPath)) return;

    const needsHydration = this.variants.length === 0 || this.variants.some(
      (variant) => !variant.mainImage || variant.priceCents == null
    );
    if (!needsHydration) return;

    try {
      const response = await fetch(`${productPath}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Shopify product request failed with ${response.status}`);
      }

      const product = await response.json();
      const shopifyVariants = Array.isArray(product.variants) ? product.variants : [];

      if (this.variants.length === 0) {
        this.variants = shopifyVariants.map((variant) => this.normalizeVariant(variant));
        return;
      }

      this.variants = this.variants.map((variant) => {
        const shopifyVariant = shopifyVariants.find(
          (candidate) => String(candidate.id) === variant.id
        );

        if (!shopifyVariant) return variant;

        const hydrated = this.normalizeVariant(shopifyVariant);
        return {
          ...hydrated,
          ...variant,
          available: shopifyVariant.available !== false,
          cardImage: variant.cardImage || hydrated.cardImage,
          mainImage: variant.mainImage || hydrated.mainImage,
          thumbnailImage: variant.thumbnailImage || hydrated.thumbnailImage,
          imageAlt: variant.imageAlt || hydrated.imageAlt,
          mediaId: variant.mediaId || hydrated.mediaId,
          priceCents: variant.priceCents ?? hydrated.priceCents,
          compareAtPriceCents:
            variant.compareAtPriceCents ?? hydrated.compareAtPriceCents,
          priceFormatted: variant.priceFormatted || hydrated.priceFormatted,
          compareAtPriceFormatted:
            variant.compareAtPriceFormatted || hydrated.compareAtPriceFormatted
        };
      });
    } catch (error) {
      console.warn('Unable to load Shopify product data:', error);
    }
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          --variant-text-color: var(--color-foreground, 51, 51, 51);
          --variant-border-color: var(--color-border, 224, 224, 224);
          --variant-active-bg: var(--color-button, 0, 0, 0);
          --variant-active-text: var(--color-button-text, 255, 255, 255);
        }

        .product-variants {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
          margin: 16px 0;
        }

        .variant-card {
          display: flex;
          width: 100%;
          min-width: 0;
          align-items: center;
          justify-content: flex-start;
          padding: 4px;
          border: 2px solid rgb(var(--variant-border-color));
          border-radius: 8px;
          color: inherit;
          background-color: inherit;
          cursor: pointer;
          font: inherit;
          text-align: left;
          transition: border-color 0.2s ease, background-color 0.2s ease;
        }

        .variant-card:hover:not(:disabled) {
          border-color: rgb(var(--variant-active-bg));
        }

        .variant-card.active {
          border-color: rgb(var(--variant-active-bg));
          background-color: rgb(var(--variant-active-bg));
        }

        .variant-card:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 2px;
        }

        .variant-card:disabled {
          border-color: rgba(var(--variant-text-color), 0.3);
          background-color: rgba(var(--variant-text-color), 0.05);
          cursor: not-allowed;
          opacity: 0.6;
        }

        .variant-image {
          width: 45px;
          height: 45px;
          margin-right: 12px;
          border-radius: 4px;
          flex-shrink: 0;
          object-fit: cover;
        }

        .variant-image-placeholder {
          display: flex;
          width: 60px;
          height: 45px;
          margin-right: 12px;
          border-radius: 4px;
          align-items: center;
          justify-content: center;
          background-color: rgba(var(--variant-text-color), 0.1);
          flex-shrink: 0;
        }

        .variant-image-placeholder svg {
          width: 30px;
          height: 30px;
          opacity: 0.5;
        }

        .variant-title {
          min-width: 0;
          color: rgb(var(--variant-text-color));
          flex: 1;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.3;
          overflow-wrap: anywhere;
        }

        .variant-card.active .variant-title {
          color: rgb(var(--variant-active-text));
          font-weight: 600;
        }

        .variant-card:disabled .variant-title {
          color: rgba(var(--variant-text-color), 0.5);
          text-decoration: line-through;
        }

        @media screen and (max-width: 749px) {
          .product-variants {
            grid-template-columns: 1fr;
          }
        }
      </style>
      <div class="product-variants" role="group" aria-label="Product variants">
        ${this.variants.map((variant) => this.renderVariantCard(variant)).join('')}
      </div>
    `;
  }

  renderVariantCard(variant) {
    const isActive = variant.id === this.selectedVariantId;
    const title = this.escapeHtml(variant.title);
    const image = variant.cardImage
      ? `<img class="variant-image" src="${this.escapeAttribute(variant.cardImage)}" alt="${this.escapeAttribute(variant.imageAlt || variant.title)}" loading="lazy">`
      : `
        <span class="variant-image-placeholder" aria-hidden="true">
          <svg viewBox="0 0 40 30" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="8" cy="22" r="6"></circle>
            <circle cx="32" cy="22" r="6"></circle>
            <path d="M8 16 L14 4 L26 4 L22 16"></path>
          </svg>
        </span>`;

    return `
      <button
        type="button"
        class="variant-card${isActive ? ' active' : ''}"
        data-variant-id="${this.escapeAttribute(variant.id)}"
        aria-pressed="${isActive}"
        ${variant.available ? '' : 'disabled'}
      >
        ${image}
        <span class="variant-title">${title}</span>
      </button>`;
  }

  handleCardClick(event) {
    const card = event.target.closest('.variant-card');
    if (!card || card.disabled) return;
    this.selectVariant(card.dataset.variantId);
  }

  selectVariant(variantId, options = {}) {
    const variant = this.findVariant(variantId);
    if (!variant || !variant.available) return;

    const settings = {
      updateUrl: true,
      dispatchEvent: true,
      ...options
    };

    this.selectedVariantId = variant.id;
    this.updateSelectedVariant();

    if (settings.updateUrl) {
      this.updateURL(variant.id);
    }

    this.updateFormInputs(variant.id);
    this.updateVariantContent(variant);

    if (settings.dispatchEvent) {
      this.dispatchEvent(new CustomEvent('variant-change', {
        bubbles: true,
        composed: true,
        detail: {
          variantId: variant.id,
          available: variant.available,
          variant
        }
      }));
    }
  }

  updateSelectedVariant() {
    this.shadowRoot.querySelectorAll('.variant-card').forEach((card) => {
      const isActive = card.dataset.variantId === this.selectedVariantId;
      card.classList.toggle('active', isActive);
      card.setAttribute('aria-pressed', String(isActive));
    });
  }

  updateVariantContent(variant) {
    this.updatePrice(variant);
    this.updateGallery(variant);
    this.updateAvailability(variant);
    this.updatePaymentMessaging(variant);
  }

  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  updateFormInputs(variantId) {
    const root = this.getProductRoot();
    const forms = root.querySelectorAll('form[action*="/cart/add"]');

    forms.forEach((form) => {
      form.querySelectorAll('[name="id"]').forEach((input) => {
        input.value = variantId;
        input.disabled = false;
      });
    });

    const productSelect = root.querySelector('#product-select') ||
      document.getElementById('product-select');

    if (productSelect) {
      productSelect.value = variantId;
    }
  }

  updatePrice(variant) {
    const root = this.getProductRoot();
    const priceContainer = root.querySelector('#product-v2-price') ||
      document.getElementById('product-v2-price');
    const price = variant.priceFormatted || this.formatMoney(variant.priceCents);
    const compareAtPrice = variant.compareAtPriceFormatted ||
      this.formatMoney(variant.compareAtPriceCents);

    if (!price) return;

    if (priceContainer) {
      const onSale = Boolean(
        compareAtPrice &&
        compareAtPrice !== price &&
        (
          variant.compareAtPriceCents == null ||
          variant.priceCents == null ||
          variant.compareAtPriceCents > variant.priceCents
        )
      );

      priceContainer.innerHTML = onSale
        ? `
          <div class="price price--large product-v2-price price--on-sale${variant.available ? '' : ' price--sold-out'}">
            <div class="price__container">
              <div class="price__sale">
                <span class="visually-hidden visually-hidden--inline">Regular price</span>
                <s class="price-item price-item--regular">${this.escapeHtml(compareAtPrice)}</s>
                <span class="visually-hidden visually-hidden--inline">Sale price</span>
                <span class="price-item price-item--sale price-item--last">${this.escapeHtml(price)}</span>
              </div>
            </div>
          </div>`
        : `
          <div class="price price--large product-v2-price${variant.available ? '' : ' price--sold-out'}">
            <div class="price__container">
              <div class="price__regular">
                <span class="visually-hidden visually-hidden--inline">Regular price</span>
                <span class="price-item price-item--regular">${this.escapeHtml(price)}</span>
              </div>
            </div>
          </div>`;
      return;
    }

    const regularPrice = root.querySelector(
      '[data-product-price], .price__regular .price-item--regular, .product__price'
    );
    if (regularPrice) {
      regularPrice.textContent = price;
    }
  }

  updateGallery(variant) {
    if (!variant.mainImage) return;

    const root = this.getProductRoot();
    const gallery = root.querySelector('#product-media-gallery') ||
      document.getElementById('product-media-gallery');

    if (gallery && gallery.hasAttribute('data-images')) {
      try {
        const images = JSON.parse(gallery.getAttribute('data-images') || '[]');
        const featuredImage = {
          src: variant.mainImage,
          alt: variant.imageAlt || variant.title,
          thumbSrc: variant.thumbnailImage || this.withImageWidth(variant.mainImage, 120)
        };
        const featuredKey = this.imageKey(featuredImage.src);
        const remainingImages = images.filter(
          (image) => this.imageKey(image.src) !== featuredKey
        );

        gallery.setAttribute('data-images', JSON.stringify([featuredImage, ...remainingImages]));
        return;
      } catch (error) {
        console.warn('Unable to update product-media-gallery:', error);
      }
    }

    if (variant.mediaId) {
      const media = root.querySelector(
        `[data-media-id="${CSS.escape(variant.mediaId)}"], ` +
        `[data-media-id$="-${CSS.escape(variant.mediaId)}"]`
      );
      const mediaButton = media && media.querySelector('button, [role="button"]');
      if (mediaButton) {
        mediaButton.click();
        return;
      }
    }

    const mainImage = root.querySelector(
      '[data-product-featured-image], .product__media-item.is-active img, #ProductPhotoImg'
    );
    if (mainImage) {
      mainImage.src = variant.mainImage;
      mainImage.removeAttribute('srcset');
      mainImage.alt = variant.imageAlt || variant.title;
    }
  }

  updateAvailability(variant) {
    if (
      window.addToCartInstance &&
      typeof window.addToCartInstance.updateButtonAvailability === 'function'
    ) {
      window.addToCartInstance.updateButtonAvailability(variant.available);
    }

    const root = this.getProductRoot();
    root.querySelectorAll(
      'form[action*="/cart/add"] [type="submit"], #add-to-cart-btn'
    ).forEach((button) => {
      button.disabled = !variant.available;
      button.setAttribute('aria-disabled', String(!variant.available));
    });
  }

  updatePaymentMessaging(variant) {
    if (variant.priceCents == null) return;

    const root = this.getProductRoot();
    root.querySelectorAll('klarna-placement[data-purchase-amount]').forEach((placement) => {
      placement.setAttribute('data-purchase-amount', String(variant.priceCents));
    });
  }

  getProductRoot() {
    return this.closest('product-info, section, .shopify-section') || document;
  }

  getSelectedVariant() {
    return this.findVariant(this.selectedVariantId);
  }

  getSelectedVariantId() {
    return this.selectedVariantId;
  }

  setSelectedVariant(variantId) {
    this.selectVariant(variantId);
  }

  findVariant(variantId) {
    if (variantId == null) return null;
    return this.variants.find((variant) => variant.id === String(variantId)) || null;
  }

  formatMoney(cents) {
    if (cents == null || Number.isNaN(Number(cents))) return '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(Number(cents));
      } catch (error) {
        // Fall through to Intl when a theme-specific formatter rejects the call.
      }
    }

    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      document.querySelector('meta[property="og:price:currency"]')?.content ||
      'GBP';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency
      }).format(Number(cents) / 100);
    } catch (error) {
      return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
    }
  }

  toCents(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
    if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
      return Number(value.trim());
    }
    return null;
  }

  formattedMoney(value) {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    return trimmed && !/^\d+$/.test(trimmed) ? trimmed : '';
  }

  firstString(...values) {
    const match = values.find((value) => typeof value === 'string' && value.trim());
    return match ? match.trim() : '';
  }

  withImageWidth(source, width) {
    if (!source) return '';
    if (/([?&])width=\d+/.test(source)) {
      return source.replace(/([?&])width=\d+/, `$1width=${width}`);
    }
    return `${source}${source.includes('?') ? '&' : '?'}width=${width}`;
  }

  imageKey(source) {
    return String(source || '')
      .replace(/([?&])width=\d+/, '$1')
      .replace(/[?&]$/, '');
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }
}

if (!customElements.get('variant-cards-v2')) {
  customElements.define('variant-cards-v2', VariantCardsV2);
}
